"use client";

import { useState } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  HINT,
  TABLE,
  TD,
  TH,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseIntegerInput, parseNumberInput } from "@/lib/parse-number";
import {
  type Equilibrium,
  type HardyWeinbergTest,
  type KnownKind,
  oneIn,
  parseFrequency,
  solveFromKnown,
  testHardyWeinberg,
} from "@/lib/tool-kit/biology-genetics/hardy-weinberg";

/** English strings. A translated content file overrides these through `ui`. */
export const HARDY_WEINBERG_CALCULATOR_STRINGS = {
  modeLabel: "What do you want to do?",
  modeSolve: "Find frequencies from one known value",
  modeTest: "Test genotype counts for equilibrium",
  knownLabel: "What do you know?",
  knownQq: "q² — frequency of the recessive phenotype (aa)",
  knownQ: "q — frequency of the recessive allele (a)",
  knownP: "p — frequency of the dominant allele (A)",
  knownPp: "p² — frequency of homozygous dominant (AA)",
  knownPq: "2pq — frequency of heterozygotes (Aa)",
  valueLabel: "Value",
  valueHint:
    "A decimal (0.0004), a percentage (0.04%), or a fraction (1/2500). Between 0 and 1.",
  sizeLabel: "Population size (optional)",
  sizeHint: "Add a size to turn each frequency into an expected head count.",
  results: "Results",
  solutionHeading: "Solution {n}: p = {p}",
  solutionNote:
    "A heterozygote frequency fits two allele frequencies, because 2pq is the same when p and q swap. Which allele is called A is your choice.",
  quantityColumn: "Quantity",
  frequencyColumn: "Frequency",
  percentColumn: "Percent",
  countColumn: "Expected count",
  alleleP: "p — allele A",
  alleleQ: "q — allele a",
  genotypePp: "p² — genotype AA",
  genotypePq: "2pq — genotype Aa",
  genotypeQq: "q² — genotype aa",
  dominantPhenotype: "Dominant phenotype (AA + Aa)",
  carrierOneIn: "Heterozygotes: 1 in {value}",
  recessiveOneIn: "Recessive homozygotes: 1 in {value}",
  checkSum: "p + q = {sum}, and p² + 2pq + q² = {total}",
  errorValue:
    "Enter a frequency between 0 and 1, such as 0.0004, 0.04% or 1/2500.",
  errorHet:
    "A heterozygote frequency cannot exceed 0.5 (50%), the most 2pq can reach.",
  errorSize: "Population size must be a whole number of 1 or more.",
  promptValue: "Enter a value to see the frequencies.",
  countAa: "Observed AA (homozygous dominant)",
  countAb: "Observed Aa (heterozygous)",
  countBb: "Observed aa (homozygous recessive)",
  countsHint: "Whole numbers of individuals, zero allowed.",
  alphaLabel: "Significance level (α)",
  tryExample: "Try an example",
  genotypeColumn: "Genotype",
  observedColumn: "Observed",
  expectedColumn: "Expected",
  contributionColumn: "(O − E)² / E",
  alleleFrequencies: "Allele frequencies: p = {p}, q = {q}",
  chiSquare: "χ²",
  df: "Degrees of freedom",
  pValue: "p-value (χ²)",
  exactP: "Exact p-value",
  n: "Sample size (N)",
  heterozygosity: "Heterozygosity: observed {observed}, expected {expected}",
  inbreeding: "F = (Hₑ − Hₒ) / Hₑ = {f}",
  decisionReject:
    "Reject equilibrium at α = {alpha}: the {test} p = {p} is below α — genotype counts differ from Hardy–Weinberg proportions.",
  decisionKeep:
    "Do not reject equilibrium at α = {alpha}: the {test} p = {p} is not below α — counts are consistent with Hardy–Weinberg proportions.",
  testChi: "χ²",
  testExact: "exact",
  smallExpected:
    "The smallest expected count is {min}, below 5, so the χ² approximation is unreliable. The decision uses the exact test.",
  exactSkipped: "The exact test was skipped because the sample is very large.",
  copyResult: "Copy result",
  copied: "Copied!",
  summary:
    "N = {n}; p = {p}, q = {q}; χ²(1) = {chi}, p = {pValue}; exact p = {exact}",
  promptCounts: "Enter genotype counts to run the test.",
  errorCounts: "Counts must be whole numbers of zero or more.",
  errorEmpty: "At least one count must be above zero.",
  errorMonomorphic:
    "Only one allele is present, so there are no expected heterozygotes and the test is undefined.",
} as const;

type Strings = typeof HARDY_WEINBERG_CALCULATOR_STRINGS;
type Fmt = (value: number, options?: Intl.NumberFormatOptions) => string;
type Mode = "solve" | "test";

const SIG: Intl.NumberFormatOptions = { maximumSignificantDigits: 6 };
const ALPHAS = ["0.1", "0.05", "0.01"] as const;
const EXAMPLE_COUNTS = ["90", "60", "50"] as const;

function knownOptions(strings: Strings) {
  return [
    { value: "qq", label: strings.knownQq },
    { value: "q", label: strings.knownQ },
    { value: "p", label: strings.knownP },
    { value: "pp", label: strings.knownPp },
    { value: "pq", label: strings.knownPq },
  ] as const;
}

function SolutionBlock({
  solution,
  heading,
  size,
  strings,
  fmt,
}: {
  solution: Equilibrium;
  heading: string | null;
  size: number | null;
  strings: Strings;
  fmt: Fmt;
}) {
  const rows = [
    [strings.alleleP, solution.p],
    [strings.alleleQ, solution.q],
    [strings.genotypePp, solution.pp],
    [strings.genotypePq, solution.pq],
    [strings.genotypeQq, solution.qq],
  ] as const;
  const carriers = oneIn(solution.pq);
  const affected = oneIn(solution.qq);
  return (
    <div className={`${CARD} space-y-4`}>
      {heading ? <h3 className={WIDGET_H3}>{heading}</h3> : null}
      <StatGrid>
        <StatCard label={strings.alleleP} value={fmt(solution.p, SIG)} />
        <StatCard label={strings.alleleQ} value={fmt(solution.q, SIG)} />
        <StatCard label={strings.genotypePq} value={fmt(solution.pq, SIG)} />
      </StatGrid>
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className={TABLE}>
          <thead>
            <tr>
              <th className={TH} scope="col">
                {strings.quantityColumn}
              </th>
              <th className={`${TH} text-right`} scope="col">
                {strings.frequencyColumn}
              </th>
              <th className={`${TH} text-right`} scope="col">
                {strings.percentColumn}
              </th>
              {size === null ? null : (
                <th className={`${TH} text-right`} scope="col">
                  {strings.countColumn}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map(([label, value], index) => (
              <tr key={label}>
                <td className={TD}>{label}</td>
                <td className={`${TD} text-right`}>{fmt(value, SIG)}</td>
                <td className={`${TD} text-right`}>{fmt(value * 100, SIG)}%</td>
                {size === null ? null : (
                  <td className={`${TD} text-right`}>
                    {fmt(index < 2 ? value * size * 2 : value * size, {
                      maximumFractionDigits: 2,
                    })}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ul className="space-y-1 text-foreground text-sm">
        <li>
          {strings.dominantPhenotype}: {fmt(solution.pp + solution.pq, SIG)}
        </li>
        {carriers === null ? null : (
          <li>
            {t(strings.carrierOneIn, {
              value: fmt(carriers, { maximumFractionDigits: 1 }),
            })}
          </li>
        )}
        {affected === null ? null : (
          <li>
            {t(strings.recessiveOneIn, {
              value: fmt(affected, { maximumFractionDigits: 1 }),
            })}
          </li>
        )}
        <li className={HINT}>
          {t(strings.checkSum, {
            sum: fmt(solution.p + solution.q, SIG),
            total: fmt(solution.pp + solution.pq + solution.qq, SIG),
          })}
        </li>
      </ul>
    </div>
  );
}

function SolveSection({ strings, fmt }: { strings: Strings; fmt: Fmt }) {
  const [kind, setKind] = useState<KnownKind>("qq");
  const [raw, setRaw] = useState("1/2500");
  const [sizeRaw, setSizeRaw] = useState("");
  const value = parseFrequency(raw, parseNumberInput);
  const size = sizeRaw.trim() === "" ? null : parseIntegerInput(sizeRaw);
  const sizeBad = sizeRaw.trim() !== "" && (size === null || size < 1);
  const outcome = value === null ? null : solveFromKnown(kind, value);

  let message: string | null = null;
  if (raw.trim() === "") {
    message = strings.promptValue;
  } else if (outcome === null || (!outcome.ok && outcome.reason === "range")) {
    message = strings.errorValue;
  } else if (!outcome.ok) {
    message = strings.errorHet;
  }
  const solutions = outcome?.ok ? outcome.solutions : [];
  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          id="hw-known"
          label={strings.knownLabel}
          onChange={setKind}
          options={knownOptions(strings)}
          value={kind}
        />
        <TextField
          hint={strings.valueHint}
          id="hw-value"
          label={strings.valueLabel}
          mono
          onChange={setRaw}
          value={raw}
        />
        <TextField
          hint={strings.sizeHint}
          id="hw-size"
          label={strings.sizeLabel}
          mono
          numeric
          onChange={setSizeRaw}
          value={sizeRaw}
        />
      </div>
      {sizeBad ? <ErrorNotice>{strings.errorSize}</ErrorNotice> : null}
      {message ? (
        <Notice>{message}</Notice>
      ) : (
        <div aria-live="polite" className="space-y-4">
          <h2 className={WIDGET_H2}>{strings.results}</h2>
          {solutions.length > 1 ? (
            <Notice>{strings.solutionNote}</Notice>
          ) : null}
          {solutions.map((solution, index) => (
            <SolutionBlock
              fmt={fmt}
              heading={
                solutions.length > 1
                  ? t(strings.solutionHeading, {
                      n: fmt(index + 1),
                      p: fmt(solution.p, SIG),
                    })
                  : null
              }
              key={solution.p}
              size={sizeBad ? null : size}
              solution={solution}
              strings={strings}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function TestResults({
  r,
  alpha,
  strings,
  fmt,
}: {
  r: HardyWeinbergTest;
  alpha: number;
  strings: Strings;
  fmt: Fmt;
}) {
  const small = r.smallestExpected < 5;
  const usesExact = small && r.exactP !== null;
  const primary = usesExact ? (r.exactP as number) : r.pValue;
  const decisionTemplate =
    primary < alpha ? strings.decisionReject : strings.decisionKeep;
  const labels = [strings.genotypePp, strings.genotypePq, strings.genotypeQq];
  const pText = (value: number) =>
    value < 0.0001 ? "< 0.0001" : fmt(value, { maximumFractionDigits: 4 });
  const copy = t(strings.summary, {
    n: fmt(r.n),
    p: fmt(r.p, SIG),
    q: fmt(r.q, SIG),
    chi: fmt(r.chiSquare, { maximumFractionDigits: 3 }),
    pValue: pText(r.pValue),
    exact: r.exactP === null ? "—" : pText(r.exactP),
  });
  return (
    <div aria-live="polite" className="space-y-4">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.chiSquare}
          value={fmt(r.chiSquare, { maximumFractionDigits: 3 })}
        />
        <StatCard label={strings.pValue} value={pText(r.pValue)} />
        <StatCard
          label={strings.exactP}
          value={r.exactP === null ? "—" : pText(r.exactP)}
        />
      </StatGrid>
      <p className="text-foreground text-sm">
        {t(strings.alleleFrequencies, { p: fmt(r.p, SIG), q: fmt(r.q, SIG) })}
        {" · "}
        {strings.n}: {fmt(r.n)} · {strings.df}: {fmt(r.df)}
      </p>
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className={TABLE}>
          <thead>
            <tr>
              <th className={TH} scope="col">
                {strings.genotypeColumn}
              </th>
              <th className={`${TH} text-right`} scope="col">
                {strings.observedColumn}
              </th>
              <th className={`${TH} text-right`} scope="col">
                {strings.expectedColumn}
              </th>
              <th className={`${TH} text-right`} scope="col">
                {strings.contributionColumn}
              </th>
            </tr>
          </thead>
          <tbody>
            {labels.map((label, i) => (
              <tr key={label}>
                <td className={TD}>{label}</td>
                <td className={`${TD} text-right`}>{fmt(r.observed[i])}</td>
                <td className={`${TD} text-right`}>
                  {fmt(r.expected[i], { maximumFractionDigits: 2 })}
                </td>
                <td className={`${TD} text-right`}>
                  {fmt(r.contributions[i], { maximumFractionDigits: 3 })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-foreground text-sm">
        {t(strings.heterozygosity, {
          observed: fmt(r.observedHeterozygosity, SIG),
          expected: fmt(r.expectedHeterozygosity, SIG),
        })}
        {r.inbreedingCoefficient === null
          ? null
          : ` · ${t(strings.inbreeding, { f: fmt(r.inbreedingCoefficient, { maximumFractionDigits: 3 }) })}`}
      </p>
      {small ? (
        <Notice>
          {r.exactP === null
            ? strings.exactSkipped
            : t(strings.smallExpected, {
                min: fmt(r.smallestExpected, { maximumFractionDigits: 2 }),
              })}
        </Notice>
      ) : null}
      <p className="font-medium text-foreground text-sm">
        {t(decisionTemplate, {
          alpha: fmt(alpha),
          test: usesExact ? strings.testExact : strings.testChi,
          p: pText(primary),
        })}
      </p>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyResult}
        text={copy}
      />
    </div>
  );
}

function TestSection({ strings, fmt }: { strings: Strings; fmt: Fmt }) {
  const [counts, setCounts] = useState<[string, string, string]>(["", "", ""]);
  const [alpha, setAlpha] = useState<string>("0.05");
  const parsed = counts.map((c) =>
    c.trim() === "" ? 0 : parseIntegerInput(c)
  );
  const invalid = parsed.some((v) => v === null || v < 0);
  const anyText = counts.some((c) => c.trim() !== "");
  const numbers = parsed as [number, number, number];
  const outcome = !invalid && anyText ? testHardyWeinberg(...numbers) : null;

  function setCount(index: number, value: string) {
    setCounts((previous) => {
      const next: [string, string, string] = [...previous];
      next[index] = value;
      return next;
    });
  }

  let message: string | null = null;
  if (invalid) {
    message = strings.errorCounts;
  } else if (!anyText) {
    message = strings.promptCounts;
  } else if (outcome && !outcome.ok) {
    message =
      outcome.reason === "empty"
        ? strings.errorEmpty
        : strings.errorMonomorphic;
  }
  const fields = [strings.countAa, strings.countAb, strings.countBb];
  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-3">
        {fields.map((label, index) => (
          <TextField
            hint={index === 0 ? strings.countsHint : undefined}
            id={`hw-count-${index}`}
            key={label}
            label={label}
            mono
            numeric
            onChange={(value) => setCount(index, value)}
            value={counts[index]}
          />
        ))}
      </div>
      <div className="flex flex-wrap items-end gap-4">
        <div className="w-full max-w-xs">
          <SelectField
            id="hw-alpha"
            label={strings.alphaLabel}
            onChange={setAlpha}
            options={ALPHAS.map((value) => ({
              value,
              label: fmt(Number(value)),
            }))}
            value={alpha}
          />
        </div>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setCounts([...EXAMPLE_COUNTS])}
          type="button"
        >
          {strings.tryExample}
        </button>
      </div>
      {message ? <Notice>{message}</Notice> : null}
      {outcome?.ok ? (
        <TestResults
          alpha={Number(alpha)}
          fmt={fmt}
          r={outcome.result}
          strings={strings}
        />
      ) : null}
    </div>
  );
}

export default function HardyWeinbergCalculatorWidget() {
  const strings = useToolUi(HARDY_WEINBERG_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("solve");
  return (
    <div className="space-y-6">
      <PillGroup
        label={strings.modeLabel}
        name="hw-mode"
        onChange={setMode}
        options={[
          { value: "solve", label: strings.modeSolve },
          { value: "test", label: strings.modeTest },
        ]}
        value={mode}
      />
      {mode === "solve" ? (
        <SolveSection fmt={fmt} strings={strings} />
      ) : (
        <TestSection fmt={fmt} strings={strings} />
      )}
    </div>
  );
}
